// Event-type → color/label mapping for the activity feed pills.
// Feb 2026 — Pruned dead filters (`auth.logout`, `checkout.abandoned`,
// `push.received`, `push.clicked`, `promo.applied`, `promo.rejected`,
// `notification.permission_requested`, `device.fingerprint`) — no client or
// server code currently emits them, so showing the chip just produces empty
// results. Keep this map in sync with actual emit sites — when a new event
// is wired, add it here.
export const EVENT_STYLE = {
  'auth.login':                 { color: '#33FF57', label: 'LOGIN' },
  'auth.register':              { color: '#FFD700', label: 'NEW SIGNUP' },
  'page.viewed':                { color: '#7FD8FF', label: 'PAGE' },
  'product.viewed':             { color: '#7FD8FF', label: 'PRODUCT' },
  'cart.add':                   { color: '#33FF57', label: 'CART+' },
  'cart.remove':                { color: '#FF8C42', label: 'CART−' },
  'cart.abandon':               { color: '#FF4D6D', label: 'CART ABANDON' },
  'checkout.started':           { color: '#FFD700', label: 'CHECKOUT' },
  'order.placed':               { color: '#33FF57', label: 'ORDER+' },
  'order.status_confirmed':     { color: '#33FF57', label: 'CONFIRMED' },
  'order.status_preparing':     { color: '#FFD700', label: 'PREPARING' },
  'order.status_out_for_delivery': { color: '#7FD8FF', label: 'OUT' },
  'order.status_delivered':     { color: '#33FF57', label: 'DELIVERED' },
  'order.status_cancelled':     { color: '#FF4D6D', label: 'CANCELLED' },
  'order.tracked_viewed':       { color: '#7FD8FF', label: 'TRACKING' },
  'search.performed':           { color: '#7FD8FF', label: 'SEARCH' },
  'referral.share_clicked':     { color: '#FFD700', label: 'REFER SHARE' },
  'referral.code_copied':       { color: '#FFD700', label: 'REFER COPY' },
  'region.zip_lookup':          { color: '#aaa',    label: 'ZIP LOOKUP' },
  'pwa.install_prompt_shown':   { color: '#aaa',    label: 'PWA PROMPT' },
  'pwa.install_accepted':       { color: '#33FF57', label: 'PWA INSTALL' },
  'pwa.install_dismissed':      { color: '#888',    label: 'PWA SKIP' },
  'notification.permission_granted':   { color: '#33FF57', label: 'NOTIF YES' },
  'notification.permission_denied':    { color: '#FF4D6D', label: 'NOTIF NO' },
};

export function styleForEvent(et) {
  return EVENT_STYLE[et] || { color: '#888', label: et.toUpperCase() };
}

export function fmtRelative(iso) {
  if (!iso) return '—';
  try {
    // Backend often returns naive ISO strings (no `Z` / offset) for UTC times.
    // JS would parse those as LOCAL — making everything look like "just now"
    // for any user not in UTC. Normalise: if no timezone marker, treat as UTC.
    let s = String(iso);
    if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) s = s + 'Z';
    const ts = new Date(s).getTime();
    if (!Number.isFinite(ts)) return iso;
    const diff = Date.now() - ts;
    if (diff < 0 && diff > -60_000) return 'just now';   // tiny clock skew
    if (diff < 0) return new Date(s).toLocaleString();   // future timestamp — just print it
    if (diff < 5_000) return 'just now';
    if (diff < 60_000) return `${Math.floor(diff / 1000)}s ago`;
    if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
    if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
    if (diff < 604_800_000) return `${Math.floor(diff / 86_400_000)}d ago`;
    return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch (_e) {
    return iso;
  }
}

export function summarize(payload, eventType, withPage = false) {
  if (eventType === 'page.viewed') {
    const raw = payload && (payload.route || payload.path);
    const page = raw && String(raw).replace(/^\/+/, '').replace(/[_/-]+/g, ' ').trim();
    const pretty = page ? page.charAt(0).toUpperCase() + page.slice(1) : 'Home';
    return withPage && raw ? `Page view · ${pretty}` : 'Page view';
  }
  if (!payload || typeof payload !== 'object') return '';
  const parts = [];
  if (payload.name) parts.push(payload.name);
  if (payload.route) parts.push(payload.route);
  if (payload.path && !payload.route) parts.push(payload.path);
  if (payload.size) parts.push(payload.size);
  if (typeof payload.qty === 'number') parts.push(`×${payload.qty}`);
  if (payload.category) parts.push(`[${payload.category}]`);
  if (payload.order_id) parts.push(`#${String(payload.order_id).slice(0, 8).toUpperCase()}`);
  if (eventType === 'auth.register' && payload.referrer_code) parts.push(`ref=${payload.referrer_code}`);
  if (eventType === 'order.placed' && typeof payload.basket_value === 'number') {
    parts.push(`$${Number(payload.basket_value ?? 0).toFixed(2)}`);
  }
  return parts.join(' · ');
}

export function roleColor(role) {
  if (role === 'super_admin') return '#FFD700';
  if (role === 'dispatcher') return '#7FD8FF';
  if (role === 'driver') return '#FF8C42';
  return '#33FF57';
}

